<template>
  <div>
    <v-row no-gutters v-if="title" class="pb-3">
      <v-badge
        overlap
        :content="computedListItems.length"
        :value="showTitleCountBadge"
        :color="titleCountBadgeColor"
        :offset-x="-1"
      >
        <v-col cols="auto" class="pa-0 text-subtitle-2">
          {{ title }}
        </v-col>
      </v-badge>
    </v-row>
    <v-list
      v-if="computedListItems.length > 0"
      class="py-0 ribbon-list-list"
      :class="{ 'ribbon-list-list-raw': styleType === 'raw' || styleType === 'rawHigher' }"
      dense
    >
      <v-list-item
        v-for="(item, index) in paginationListItems"
        :key="`ribbonItem-${index}`"
        v-on="item.listeners"
        :inactive="!item.hoverable"
        :ripple="!!item.hoverable"
        :class="[
          disablePaddings ? 'px-0' : 'px-2',
          item.hoverable && 'hover-list-element',
          'ribbon-list-element',
          (styleType === 'raw' || styleType === 'rawHigher') && 'ribbon-list-element-raw',
          styleType === 'rawHigher' && 'ribbon-list-element-raw-higher',
          styleType === 'defaultHigher' && 'ribbon-list-element-default-higher',
          fixedHeight && 'ribbon-list-element-fixed-height',
          ...(item.classes ? item.classes : []),
        ]"
      >
        <v-list-item-content>
          <v-list-item-title class="text-body-2">
            <v-row no-gutters class="flex-nowrap" align="center">
              <slot :item="item" :index="index">
                <v-col
                  v-bind="{ ...(leftCols || leftCols === 0 ? { cols: leftCols } : {}) }"
                  :style="`${noFlex ? '' : 'flex: 1 1 auto;'}min-width: 0`"
                >
                  <v-row no-gutters align="center">
                    <v-col cols="auto" class="text-truncate">
                      <slot name="itemTitle-prepend" :item="item" :index="index" />
                    </v-col>
                    <v-col cols="auto">
                      <v-row no-gutters>
                        <v-col cols="auto">
                          <v-tooltip bottom color="font" max-width="300">
                            <template #activator="{ on }">
                              <div
                                :class="[
                                  'text-body-2',
                                  'ribbon-list-text',
                                  ...(item.titleClasses ? item.titleClasses : []),
                                ]"
                                v-on="on"
                              >
                                {{ item[itemTitleKey] }}
                              </div>
                            </template>
                            <div style="word-break: break-all;">
                              {{ item[itemTitleKey] }}
                            </div>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                      <v-row no-gutters v-if="item[itemSubtitleKey]">
                        <v-col style="padding-top: 0 !important">
                          <italic-subheader :straight-text="item[itemSubtitleKey]"></italic-subheader>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="auto">
                      <slot name="itemTitle-append" :item="item" :index="index" />
                    </v-col>
                  </v-row>
                </v-col>
                <v-spacer v-if="!noFlex" />
                <v-col v-bind="{ ...(rightCols || rightCols === 0 ? { cols: rightCols } : {}) }" class="pl-3">
                  <v-row no-gutters align="center" class="ribbon-list-buttons-row">
                    <slot name="itemButtons-prepend" :item="item" :index="index" />
                    <slot name="itemButtons-replace" :item="item" :index="index">
                      <v-col
                        v-for="(button, buttonIndex) in item.buttons"
                        :key="`ribbonItem-${index}-button-${buttonIndex}`"
                        cols="auto"
                      >
                        <v-tooltip :disabled="!button.tooltip" bottom color="font" max-width="300">
                          <template #activator="{ on }">
                            <v-btn
                              v-on="on"
                              icon
                              x-small
                              v-bind="button.params"
                              @click="button.clickHandler({ item, index })"
                            >
                              <v-icon>
                                {{ button.icon }}
                              </v-icon>
                            </v-btn>
                          </template>
                          <div style="word-break: break-all;">
                            {{ button.tooltip }}
                          </div>
                        </v-tooltip>
                      </v-col>
                    </slot>
                    <slot name="itemButtons-append" :item="item" :index="index" />
                  </v-row>
                </v-col>
              </slot>
            </v-row>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <template v-if="paginationSummary || paginationPages">
        <list-multiplier-toggler
          v-model="itemsMultiplier"
          v-bind="{
            ...(paginationPages
              ? { toggleOnTranslationPath: 'featureCard.seeNext', toggleOffTranslationPath: 'featureCard.seePrevious' }
              : {}),
          }"
          :all-items-length="computedListItems.length"
          :page-items-length="toggleMaxItems"
        />
      </template>
      <template v-else>
        <list-toggler
          v-if="toggleable && computedListItems.length > toggleMaxItems * itemsMultiplier"
          v-model="fullListVisible"
        />
      </template>
    </v-list>
    <italic-subheader :translation-path="noItemsTranslationPath" v-else-if="noItemsTranslationPath" />
  </div>
</template>

<script>
import ListMultiplierToggler from '@/components/ListMultiplierToggler';
import ListToggler from '@/components/ListToggler';
import ItalicSubheader from '@/components/ItalicSubheader';

export default {
  name: 'RibbonList',
  components: { ItalicSubheader, ListMultiplierToggler, ListToggler },
  computed: {
    computedListItems() {
      return this.listItems.map(listItem => {
        const listeners = this.getListeners(listItem);
        return {
          ...listItem,
          listeners,
          hoverable: this.hoverableListeners.find(listenerKey => (listeners[listenerKey] ? true : false)),
        };
      });
    },
    paginationListItems() {
      if (this.fullListVisible || !this.toggleable) {
        return this.computedListItems;
      } else if (this.paginationPages) {
        return this.computedListItems.slice(
          (this.itemsMultiplier - 1) * this.toggleMaxItems,
          this.toggleMaxItems * this.itemsMultiplier
        );
      } else {
        return this.computedListItems.slice(0, this.toggleMaxItems * this.itemsMultiplier);
      }
    },
    paginationSummary() {
      return this.paginationType === 'summary';
    },
    paginationPages() {
      return this.paginationType === 'pages';
    },
    title() {
      return this.titlePath ? this.$i18n.t(this.titlePath) : this.titleStraight;
    },
  },
  data: () => ({
    fullListVisible: false,
    itemsMultiplier: 1,
  }),
  props: {
    listItems: {
      type: Array,
      default: () => {
        return [];
      },
    },
    itemTitleKey: {
      type: String,
      default: 'title',
    },
    itemSubtitleKey: {
      type: String,
      default: 'subtitle',
    },
    toggleable: {
      type: Boolean,
      default: true,
    },
    toggleMaxItems: {
      type: Number,
      default: 4,
    },
    paginationType: {
      type: String,
      default: 'off',
      validator: v => {
        return ['off', 'pages', 'summary'].includes(v);
      },
    },
    titlePath: {
      type: String,
      default: '',
    },
    titleStraight: {
      type: String,
      default: '',
    },
    noItemsTranslationPath: {
      type: String,
      default: '',
    },
    showTitleCountBadge: {
      type: Boolean,
      default: false,
    },
    titleCountBadgeColor: {
      type: String,
      default: 'error',
    },
    noFlex: {
      type: Boolean,
      default: false,
    },
    leftCols: {
      type: [Number, String],
      default: 'auto',
    },
    rightCols: {
      type: [Number, String],
      default: 'auto',
    },
    styleType: {
      type: String,
      validator: value => {
        return ['default', 'raw', 'rawHigher', 'defaultHigher'].includes(value);
      },
      default: 'default',
    },
    hoverableListeners: {
      type: Array,
      default: () => {
        return ['click', 'mouseenter'];
      },
    },
    disablePaddings: {
      type: Boolean,
      default: false,
    },
    fixedHeight: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    getListeners(item) {
      const listeners = Object.keys(this.$listeners).reduce((total, key) => {
        return {
          ...total,
          [key]: () => {
            this.$emit(key, item);
          },
        };
      }, {});
      if (item.itemClickHandler) {
        listeners.click = () => {
          item.itemClickHandler(item);
        };
      }
      return listeners;
    },
  },
};
</script>

<style lang="scss" scoped>
.ribbon-list-element {
  cursor: default;
  text-align: left;
  min-height: 36px;
}
.ribbon-list-element:not(.ribbon-list-element-raw-higher):not(.ribbon-list-element-default-higher) {
  min-height: 36px;
}
.ribbon-list-element:not(.ribbon-list-element-raw-higher):not(.ribbon-list-element-default-higher).ribbon-list-element-fixed-height {
  max-height: 36px;
}
.ribbon-list-element-raw-higher {
  min-height: 56px;
}
.ribbon-list-element-raw-higher.ribbon-list-element-fixed-height {
  max-height: 56px;
}
.ribbon-list-element-default-higher {
  min-height: 56px;
}
.ribbon-list-element-default-higher.ribbon-list-element-fixed-height {
  max-height: 56px;
}
.ribbon-list-element:not(.ribbon-list-element-raw) {
  @include border(bottom);
}
.ribbon-list-element.ribbon-list-element-raw {
  &:not(:last-child) {
    @include border(bottom);
  }
}
.hover-list-element:hover {
  cursor: pointer;
}
.ribbon-list-list {
  border-radius: 4px;
}
.ribbon-list-list:not(.ribbon-list-list-raw) {
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px -1px 0px rgba(0, 0, 0, 0.15) !important;
}
.ribbon-list-list.ribbon-list-list-raw {
  @include border();
}
.ribbon-list-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
::v-deep {
  .v-list-item__content {
    padding: 0 !important;
  }
}
.list-element-active {
  background-color: #edf4fe !important;
  color: map-get($colors, 'primary') !important;
}
</style>
